import { keepPreviousData } from '@tanstack/react-query';
import { atom, Getter, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { loadable, unwrap } from 'jotai/utils';
import { atomWithQuery } from 'jotai-tanstack-query';
import { useMemo } from 'react';
import { OwnershipTimelineStack } from '../../../../schemas/OwnershipTimeline.schema';
import { SecurityReport } from '../../../../schemas/SecurityReport.schema';
import { getSecurityReport } from '../../../../services/queries/MaggieMetricsQueries';
import { dateToPlainDateString, isValidDate } from '../../../../util/date-utilts';
import { captureUnexpectedState } from '../../../../util/SentryUtils';
import { companyMetricsLoadableJ } from '../../../../services/state/CompanyMetricsStateJ';
import { CompanyFinanceRoundsState } from '../../../CompanyProfiles/state/CompanyFundingsState';
import { IRoundTerms, toRoundTerms } from './security-story-model';
import { ownershipTimelineEventsJ, selectEventAsOf } from '../OwnershipTimeline/state';
import { MetricsTransactionDataModel } from '../../../../schemas/MetricsTransaction.schema';
import {
  getDistinctFundIds,
  groupTransactionsByLot,
  IEquityLot,
  selectHeldLots,
  selectLotsAsOf,
  selectLotsForFund,
  selectTransactionsAsOf,
  toEquityLots,
} from './equity-holdings-model';

export type EquityHoldingsView = 'overview' | 'history';

export const SecurityReportQueryKey = 'equityHoldingsSecurityReport';

/** `null` is "All Funds". */
export const equityHoldingsFundIdJ = atomFamily((_companyId: number) => atom<number | null>(null));

/** `null` is "today" — the as-of picker's default. */
export const equityHoldingsAsOfDateJ = atomFamily((_companyId: number) => atom<Date | null>(null));

export const equityHoldingsViewJ = atomFamily((_companyId: number) => atom<EquityHoldingsView>('overview'));

/** Long enough that stepping back to a date already viewed is instant, short enough to bound the cache. */
const ReportCacheTtlMs = 60 * 60 * 1000;

/**
 * An invalid date must never reach the request. `dateToPlainDateString` reads getFullYear/getMonth/
 * getDate with no guard, so an Invalid Date — which the picker emits for something like 02/31 — would
 * serialise to the literal `NaN-NaN-NaN` and take the card down with a failed fetch. Treated as "no
 * date", i.e. today.
 */
function requestDate(asOfDate: Date | null): string | undefined {
  return asOfDate !== null && isValidDate(asOfDate) ? dateToPlainDateString(asOfDate) : undefined;
}

/**
 * `securityReport` is fetched per (company, as-of date) because the endpoint does the as-of filtering
 * server-side — unlike the ownership timeline, which returns every event at once and is filtered in
 * memory.
 *
 * One query atom per company, with the date in the `queryKey` rather than in the family key: the date
 * has to change on the *same* observer for `keepPreviousData` to hold the previous date's rows while
 * the next ones load. A family keyed by date would hand the card a different atom instead, and it
 * would blank out on every step.
 */
const securityReportQueryJ = atomFamily((companyId: number) =>
  atomWithQuery<SecurityReport[]>((get) =>
    securityReportQueryOptions(companyId, requestDate(get(equityHoldingsAsOfDateJ(companyId))))
  )
);

// Same query key as the panel at its default date, so the cache entry is shared and nothing refetches.
const securityReportTodayQueryJ = atomFamily((companyId: number) =>
  atomWithQuery<SecurityReport[]>(() => securityReportQueryOptions(companyId, undefined))
);

function securityReportQueryOptions(companyId: number, date: string | undefined) {
  return {
    queryKey: [SecurityReportQueryKey, companyId, date ?? ''],
    queryFn: async () => {
      if (companyId <= 0) {
        return [];
      }
      try {
        return await getSecurityReport({ companyIds: [companyId], date });
      } catch (err) {
        // Reported and rethrown, not swallowed into an empty array: this is the card's primary
        // source, and a failed request must surface as an error rather than as "no holdings", which
        // reads as a company that owns nothing. The ownership-timeline join is the one that degrades
        // silently — it only supplies display names and the diluted count.
        captureUnexpectedState('securityReportQueryJ: securityReport fetch failed', {
          companyId,
          date,
          err,
        });
        throw err;
      }
    },
    // Keeps the previous date's rows on screen while the next date loads, so the KPI strip never
    // flashes a row of real-looking zeros beside a fund picker with no options.
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    gcTime: ReportCacheTtlMs,
    // The card renders its own error state with a retry, so a failure should surface at once rather
    // than after three silent backoffs.
    retry: false,
  };
}

/**
 * Read as a loadable rather than awaited so the lots below stay synchronous. It is keyed by company
 * only and never refetches on a date step, so it resolves once and holds.
 */
const timelineEventsLoadableJ = atomFamily((companyId: number) =>
  loadable(ownershipTimelineEventsJ(companyId))
);

/**
 * The company's rounds, for the terms the story chips print — the cap a SAFE was bought under, the
 * post-money a conversion priced at, and whether the round qualified for QSBS.
 *
 * Unwrapped to an empty list while loading, so the join degrades like the timeline one: a round that
 * has not arrived costs the chips a term, not the card.
 */
const financeRoundsSyncJ = atomFamily((companyId: number) =>
  unwrap(CompanyFinanceRoundsState(companyId), (prev) => prev ?? [])
);

interface IEquityLotsResult {
  lots: IEquityLot[];
  /** Kept alongside the lots so History can build stories and conversion pairs from them. */
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>;
  /** Round terms by round id, joined to a transaction by its `roundId`. */
  roundTerms: Map<number, IRoundTerms>;
}

/**
 * The card's rows. Joins the security report against the ownership-timeline event for the same date,
 * which supplies each security's display name and the company's fully diluted count.
 *
 * Synchronous throughout: every source is read as a settled value (a query result, two loadables), so
 * a date step recomputes in place instead of suspending and tearing the card — tabs, filters and all —
 * back down to a spinner.
 */
const equityLotsJ = atomFamily((companyId: number) =>
  atom<IEquityLotsResult>((get) =>
    buildEquityLots(
      get,
      companyId,
      get(securityReportQueryJ(companyId)).data ?? [],
      get(equityHoldingsAsOfDateJ(companyId))
    )
  )
);

function buildEquityLots(
  get: Getter,
  companyId: number,
  reports: SecurityReport[],
  asOfDate: Date | null
): IEquityLotsResult {
  const timeline = get(timelineEventsLoadableJ(companyId));
  const events = timeline.state === 'hasData' ? timeline.data : [];
  const event = selectEventAsOf(events, asOfDate);

  // Transactions state a lot's lifecycle outright — `Investment - Write Off` — where
  // `securityReport` has aggregated that away and the card would otherwise infer it from share
  // counts this API leaves empty. Read as a loadable: the company profile already loads these, and
  // if they are not there yet the lifecycle simply falls back to the numeric inference rather than
  // holding the card up.
  const metrics = get(companyMetricsLoadableJ({ companyId }));
  const transactions = metrics.state === 'hasData' ? (metrics.data?.metrics?.at(0)?.transactions ?? []) : [];

  // The as-of date has to reach the transactions, not only the report request. The report is
  // filtered server-side, but these are the company's whole ledger: without the cut, a 2025
  // purchase and the conversion it triggered still draw a chain, a chip and a link on a 2022 as-of
  // date. `null` is "today" and is left alone, the same as the request is.
  const transactionsByLot = groupTransactionsByLot(selectTransactionsAsOf(transactions, asOfDate));

  const roundTerms = get(financeRoundsSyncJ(companyId)).reduce(
    (acc, round) => acc.set(round.id, toRoundTerms(round)),
    new Map<number, IRoundTerms>()
  );

  return {
    transactionsByLot,
    roundTerms,
    lots: selectLotsAsOf(
      toEquityLots({
        reports,
        stacksByRestructureId: indexStacks(event?.stacks ?? []),
        fullyDilutedShares: event?.fullyDilutedShares ?? null,
        transactionsByLot,
      }),
      asOfDate
    ),
  };
}

function indexStacks(stacks: OwnershipTimelineStack[]): Map<string, OwnershipTimelineStack> {
  return stacks.reduce(
    (acc, stack) => acc.set(stack.restructureId, stack),
    new Map<string, OwnershipTimelineStack>()
  );
}

export interface IHeldFundIds {
  /** Funds with a position still on the books today, by Equity Holdings' own held rule. */
  fundIds: number[];
  /** True when the report resolved and named lots, so an empty `fundIds` means fully exited. */
  hasLots: boolean;
  isLoading: boolean;
}

// Today's lots, whatever as-of date the Equity Holdings picker is set to: a position is a present-tense fact.
export const heldFundIdsJ = atomFamily((companyId: number) =>
  atom<IHeldFundIds>((get) => {
    const report = get(securityReportTodayQueryJ(companyId));
    const { lots } = buildEquityLots(get, companyId, report.data ?? [], null);
    return {
      fundIds: getDistinctFundIds(selectHeldLots(lots)),
      hasLots: lots.length > 0,
      isLoading: report.isPending,
    };
  })
);

export interface IEquityHoldingsData {
  /** Every lot for the selected fund, held or not. Security History's rows. */
  lots: IEquityLot[];
  /** The subset still on the books. Security Overview's rows. */
  heldLots: IEquityLot[];
  /** Every fund to offer in the picker, including the current selection. */
  fundIds: number[];
  /** True only on a first load with nothing to show yet — a refetch keeps the previous rows. */
  isLoading: boolean;
  /** True when the fetch failed. Distinguished from an empty response so the card can say so. */
  hasError: boolean;
  /** Re-requests after a failure. */
  retry: () => void;
  /** Transactions keyed by lot, for History's stories and conversion pairs. */
  transactionsByLot: Map<string, MetricsTransactionDataModel[]>;
  /** Round terms by round id, for the terms the story chips print. */
  roundTerms: Map<number, IRoundTerms>;
}

/**
 * Totals are deliberately not returned here. `aggregateTotals` sums exactly the lots it is given, and
 * the KPI strip has to agree with the pinned Total sitting one row beneath it, so the caller scopes
 * both from the same row set — see `EquityHoldingsPanel`.
 */
export function useEquityHoldings(companyId: number): IEquityHoldingsData {
  const { lots: allLots, transactionsByLot, roundTerms } = useAtomValue(equityLotsJ(companyId));
  const report = useAtomValue(securityReportQueryJ(companyId));
  const timeline = useAtomValue(timelineEventsLoadableJ(companyId));
  const fundId = useAtomValue(equityHoldingsFundIdJ(companyId));

  // `isPending` is false as soon as `keepPreviousData` supplies the previous date's rows, so this is
  // only ever true on a genuine first load. The timeline is included because it carries the security
  // names — resolving the report first would otherwise paint a grid of unnamed rows.
  const isLoading = report.isPending || timeline.state === 'loading';

  return useMemo(() => {
    const lots = selectLotsForFund(allLots, fundId);

    return {
      lots,
      heldLots: selectHeldLots(lots),
      // The selection is always offered, even on a date whose lots do not include it: dropping it
      // leaves the picker blank while still filtering, with no way to see or clear the filter.
      fundIds: withSelectedFund(getDistinctFundIds(allLots), fundId),
      isLoading,
      hasError: report.isError,
      retry: report.refetch,
      transactionsByLot,
      roundTerms,
    };
  }, [allLots, transactionsByLot, roundTerms, fundId, isLoading, report.isError, report.refetch]);
}

function withSelectedFund(fundIds: number[], selected: number | null): number[] {
  if (selected === null || fundIds.includes(selected)) {
    return fundIds;
  }
  return [...fundIds, selected];
}
